import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export function SettingsBilling() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const completingRef = useRef(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/billing");

        if (response.ok) {
          const data = await response.json();
          const rawBalance = data.billing?.balance || 0;
          setBalance(Math.round(rawBalance * 100) / 100);
        } else {
          console.error("Failed to fetch billing information");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    async function completeRecharge({ sessionId }: { sessionId: string }) {
      if (completingRef.current) return;
      completingRef.current = true;
      setIsLoading(true);
      try {
        const response = await fetch("/api/billing/recharge/complete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });

        if (response.ok) {
          const data = await response.json();
          const rawBalance = data.billing?.balance || 0;
          setBalance(Math.round(rawBalance * 100) / 100);
          toast.success("Balance recharged successfully");
        } else {
          const error = await response.json().catch(() => ({}));
          toast.error(error.error || "Failed to complete recharge");
          await fetchData();
          return;
        }
      } catch (error) {
        console.error("Failed to complete recharge:", error);
        toast.error("Failed to complete recharge");
        await fetchData();
        return;
      } finally {
        setSearchParams({}, { replace: true });
        setIsLoading(false);
      }
    }

    const sessionId = searchParams.get("session_id");
    const recharge = searchParams.get("recharge");

    if (recharge === "success" && sessionId) {
      completeRecharge({ sessionId });
      return;
    }

    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Balance</h3>
              <p className="text-2xl font-semibold tabular-nums">
                ${balance.toFixed(2)}
              </p>
            </div>
            <Button asChild>
              <Link to="/settings/billing/recharge">Recharge</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
